<script setup lang="ts">
// Internal dev dashboard — single self-updating page aggregating GitLab MRs + pipelines, Jira RDT/POS,
// the version matrix and profile-generation from the cockpit service. Dev-audience only, so copy is
// intentionally in English rather than i18n keys. Pending profiles/participants are read straight from
// the backends (admin token); the participant delete goes through the cockpit's backend-role account.
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useConfirm } from "primevue";
import { useToast } from "primevue/usetoast";
import { CockpitAPI, ProfileAdministrationAPI, ProfileContractorApi } from "../api";
import type {
    CockpitOverview,
    JiraIssueInfo,
    ProjectPipelineInfo,
    SonarProjectInfo,
    VersionCell,
} from "../types/CockpitTypes.ts";
import type { PendingParticipant, Profile } from "../types/PersologOnlineTypes.ts";
// Pure display helpers (severity mappers, formatters, alert/version derivation) live in a testable util.
import {
    alertItems,
    alertSeverity,
    type AreaFilter,
    ENV_ORDER,
    filterMergeRequests,
    filterPipelines,
    filterSonarProjects,
    filterVersions,
    formatAge,
    formatCoverage,
    formatStarted,
    formatTime,
    gateSeverity,
    healthSeverity,
    isFilterActive,
    parseAreaFilter,
    pendingProfileRowClass,
    pipelineSeverity,
    sourceSeverity,
    statusCategorySeverity,
    toProfile,
    versionEnvsInOrder,
    versionsForEnv as versionsForEnvOf,
} from "../utils/devCockpitFormatters.ts";

const OPENOBSERVE_URL = "https://logs.infra.persolog.online/";
const UPTIME_KUMA_URL = "https://uptime.infra.persolog.online/dashboard";
const DOCKHAND_URL = "https://dockhand.infra.persolog.online/";
const GITLAB_URL = "https://gitlab.persolog.com/";
const JIRA_URL = "https://persolog.atlassian.net/";
const NEXUS_URL = "https://nexus.persolog.com/";
const SONARQUBE_URL = "https://sonarqube.persolog.com/";
// Env-aware: dev build → dev Keycloak, staging/prod builds → prod Keycloak (same var the app authenticates against).
const KEYCLOAK_URL = import.meta.env.VITE_APP_KEYCLOAK_URL;
const POLL_INTERVAL_MS = 15000;

const overview = ref<CockpitOverview | null>(null);
const loading = ref(false);
const errorMessage = ref<string | null>(null);
const pendingProfiles = ref<Profile[]>([]);
const pendingParticipants = ref<PendingParticipant[]>([]);
const groupCharts = ref<string[]>([]);
let intervalId: ReturnType<typeof setInterval> | undefined;

// Opt-in browser notifications for newly-appeared failures / pending-profile warnings.
const notifySupported = typeof Notification !== "undefined";
const notifyPermission = ref<NotificationPermission>(notifySupported ? Notification.permission : "denied");
const notifiedKeys = new Set<string>();
let notificationsSeeded = false;

// Row selection is only used for the highlight (click to keep a row highlighted, hover to preview it).
const selectedRdt = ref<JiraIssueInfo | null>(null);
const selectedPos = ref<JiraIssueInfo | null>(null);
const selectedPipeline = ref<ProjectPipelineInfo | null>(null);
const selectedVersion = ref<VersionCell | null>(null);
const selectedSonar = ref<SonarProjectInfo | null>(null);
const selectedProfile = ref<Profile | null>(null);
const selectedParticipant = ref<PendingParticipant | null>(null);

const confirm = useConfirm();
const toast = useToast();
const route = useRoute();
const router = useRouter();

// Filter for the GitLab-based panels (MRs, main pipelines, versions, SonarQube). Kept in the URL query
// (?filter=…&area=…), so it is per viewer, survives the reload and can be shared as a link, without any
// server-side state.
const AREA_OPTIONS: Array<{ label: string; value: AreaFilter }> = [
    { label: "All", value: "all" },
    { label: "persolog Online", value: "online" },
    { label: "Internal", value: "internal" },
    { label: "Infrastructure", value: "infra" },
];
const filterText = ref(typeof route.query.filter === "string" ? route.query.filter : "");
const filterArea = ref<AreaFilter>(parseAreaFilter(route.query.area));
const projectFilter = computed(() => ({ text: filterText.value, area: filterArea.value }));
const filterActive = computed(() => isFilterActive(projectFilter.value));
const filteredMergeRequests = computed(() => filterMergeRequests(overview.value?.mergeRequests.data ?? [], projectFilter.value));
const filteredPipelines = computed(() => filterPipelines(overview.value?.pipelines.data ?? [], projectFilter.value));
const filteredVersions = computed(() => filterVersions(overview.value?.versions.data ?? [], projectFilter.value));
const filteredSonar = computed(() => filterSonarProjects(overview.value?.sonarqube.data ?? [], projectFilter.value));

watch(projectFilter, ({ text, area }) => {
    router.replace({
        query: { ...route.query, filter: text.trim() || undefined, area: area === "all" ? undefined : area },
    });
});

function clearFilter() {
    filterText.value = "";
    filterArea.value = "all";
}

/** "(12)" unfiltered, "(3 of 12)" while the filter hides rows. */
function countLabel(shown: number, total: number): string {
    return filterActive.value ? `(${shown} of ${total})` : `(${total})`;
}

async function fetchOverview() {
    loading.value = true;
    try {
        overview.value = await CockpitAPI.getOverview();
        errorMessage.value = null;
        checkNotifications(overview.value);
    } catch (err) {
        errorMessage.value = err instanceof Error ? err.message : "Failed to load overview";
    } finally {
        loading.value = false;
    }
}

async function fetchPending() {
    try {
        const [profiles, participants] = await Promise.all([
            ProfileContractorApi.fetchPendingProfiles(),
            ProfileAdministrationAPI.fetchPendingParticipants(),
        ]);
        pendingProfiles.value = [...profiles].sort((a, b) => a.id - b.id);
        pendingParticipants.value = participants;
    } catch (err) {
        // Profile generation is just one section — a failure here shouldn't blank the rest of the page.
        console.error("Failed to load pending profile-generation data", err);
    }
}

function refreshAll() {
    fetchOverview();
    fetchPending();
}

// Manual "sync now": forces the cockpit to re-poll every source (GitLab/Jira live), rather than the
// background poll which just reads the cockpit's cache. Use after resolving something you want reflected.
async function manualSync() {
    loading.value = true;
    try {
        overview.value = await CockpitAPI.forceRefresh();
        errorMessage.value = null;
        await fetchPending();
    } catch (err) {
        errorMessage.value = err instanceof Error ? err.message : "Failed to sync";
    } finally {
        loading.value = false;
    }
}

function enableNotifications() {
    if (!notifySupported) return;
    Notification.requestPermission().then((perm) => (notifyPermission.value = perm));
}

/** Notify once per newly-appeared condition. The first snapshot only seeds, so we don't burst on load. */
function checkNotifications(ov: CockpitOverview) {
    const shouldNotify = notificationsSeeded && notifySupported && notifyPermission.value === "granted";
    for (const item of alertItems(ov)) {
        if (notifiedKeys.has(item.key)) continue;
        notifiedKeys.add(item.key);
        if (shouldNotify) {
            try {
                new Notification(item.title, { body: item.body, tag: item.key });
            } catch {
                // some browsers (mobile Safari) throw on the constructor — silently skip
            }
        }
    }
    notificationsSeeded = true;
}

onMounted(() => {
    refreshAll();
    // Group-chart products need the chart-level regenerate endpoint; fetch the list once.
    ProfileAdministrationAPI.getGroupCharts()
        .then((charts) => (groupCharts.value = charts))
        .catch((err) => console.error("Failed to load group charts", err));
    intervalId = setInterval(refreshAll, POLL_INTERVAL_MS);
});
onBeforeUnmount(() => clearInterval(intervalId));

// --- Profile-generation actions (same as the Support profile-generation page, via the admin token) ---
function isGroupChart(product: string): boolean {
    return groupCharts.value.includes(product);
}

/** Regenerate a profile; deleteFirst mirrors the pending-profiles behaviour (re-create from scratch). */
async function regenerateProfile(profile: Profile, deleteFirst = true) {
    try {
        if (deleteFirst) await ProfileContractorApi.deleteProfile(profile);
        if (isGroupChart(profile.product)) {
            await ProfileContractorApi.regenerateGroupChart(profile);
        } else {
            await ProfileContractorApi.regenerateProfile(profile);
        }
        toast.add({ severity: "success", summary: "Regenerated", detail: `Profile ${profile.id} regenerated`, life: 3000 });
        await fetchPending();
    } catch (err) {
        const detail = err instanceof Error ? err.message : "Could not regenerate the profile";
        toast.add({ severity: "error", summary: "Regenerate failed", detail, life: 5000 });
    }
}

async function recalculateProfile(profile: Profile) {
    try {
        await ProfileContractorApi.recalculateProfile(profile);
        await ProfileContractorApi.regenerateProfile(profile);
        toast.add({ severity: "success", summary: "Recalculated", detail: `Profile ${profile.id} recalculated`, life: 3000 });
        await fetchPending();
    } catch (err) {
        const detail = err instanceof Error ? err.message : "Could not recalculate the profile";
        toast.add({ severity: "error", summary: "Recalculate failed", detail, life: 5000 });
    }
}

async function deleteProfile(profile: Profile) {
    try {
        await ProfileContractorApi.deleteProfile(profile);
        toast.add({ severity: "success", summary: "Deleted", detail: `Profile ${profile.id} removed`, life: 3000 });
        await fetchPending();
    } catch (err) {
        const detail = err instanceof Error ? err.message : "Could not delete the profile";
        toast.add({ severity: "error", summary: "Delete failed", detail, life: 5000 });
    }
}

function confirmDeleteParticipant(participant: { id?: number; firstName?: string; name?: string; email?: string }) {
    if (participant.id == null) return;
    const id = participant.id;
    const who = [participant.firstName, participant.name].filter(Boolean).join(" ") || participant.email || `#${id}`;
    confirm.require({
        header: "Delete participant",
        message: `Delete participant ${who}? This removes the participant and its dependencies.`,
        icon: "pi pi-exclamation-triangle",
        rejectProps: { label: "Cancel", severity: "secondary", outlined: true },
        acceptProps: { label: "Delete", severity: "danger" },
        accept: async () => {
            try {
                await CockpitAPI.deleteParticipant(id);
                toast.add({ severity: "success", summary: "Deleted", detail: `Participant ${who} removed`, life: 3000 });
                await fetchPending();
            } catch (err) {
                const detail = err instanceof Error ? err.message : "Could not delete participant";
                toast.add({ severity: "error", summary: "Delete failed", detail, life: 5000 });
            }
        },
    });
}

// Environments that actually have version targets, ordered prod → staging → dev so the prod tab is
// first (and the default active tab).
const versionEnvs = computed<string[]>(() => versionEnvsInOrder(overview.value?.versions.data ?? [], ENV_ORDER));

// The env tabs come from every target; only the rows inside a tab follow the filter.
function versionsForEnv(env: string): VersionCell[] {
    return versionsForEnvOf(filteredVersions.value, env);
}
</script>

<template>
    <div class="dev-cockpit">
        <Toast />
        <ConfirmDialog />

        <div class="flex items-center justify-between mb-4">
            <h1 class="m-0">Dev Cockpit</h1>
            <div class="flex items-center gap-2">
                <ProgressSpinner v-if="loading" style="width: 1.5rem; height: 1.5rem" strokeWidth="6" />
                <span v-if="overview" class="text-content-muted text-sm">
                    updated {{ formatTime(overview.generatedAt) }}
                </span>
                <Button
                    v-if="notifyPermission === 'default'"
                    icon="pi pi-bell"
                    text
                    rounded
                    label="Alerts"
                    title="Enable browser alerts for failures & pending-profile warnings"
                    @click="enableNotifications"
                />
                <Button
                    v-else-if="notifyPermission === 'granted'"
                    icon="pi pi-bell"
                    text
                    rounded
                    disabled
                    aria-label="Browser alerts on"
                    title="Browser alerts on"
                />
                <Button
                    icon="pi pi-refresh"
                    text
                    rounded
                    aria-label="Sync now"
                    title="Sync now — force a fresh pull from GitLab/Jira"
                    :disabled="loading"
                    @click="manualSync"
                />
            </div>
        </div>

        <Message v-if="errorMessage" severity="error" :closable="false" class="mb-4">
            Could not reach the cockpit service: {{ errorMessage }}
        </Message>

        <template v-if="overview">
            <!-- Alerts (e.g. hanging profile generation) — the only thing above pending profiles -->
            <Message
                v-for="(alert, index) in overview.alerts"
                :key="index"
                :severity="alertSeverity(alert.severity)"
                :closable="false"
                class="mb-2"
            >
                <strong>{{ alert.source }}</strong> — {{ alert.message }}
            </Message>

            <!-- 1. Pending profiles (with the profile-generation summary folded into the header) -->
            <Panel class="mb-2">
                <template #header>
                    <div class="flex items-center gap-2 flex-wrap">
                        <span class="font-bold">Pending profiles</span>
                        <span class="text-content-muted text-sm">({{ pendingProfiles.length }})</span>
                        <template v-if="overview.profileGeneration.data">
                            <Tag
                                :value="overview.profileGeneration.data.state"
                                :severity="healthSeverity(overview.profileGeneration.data.state)"
                            />
                            <span class="text-content-muted text-sm">
                                oldest {{ formatAge(overview.profileGeneration.data.oldestPendingAgeSeconds) }}
                            </span>
                        </template>
                        <Tag
                            v-else-if="overview.profileGeneration.status === 'DISABLED'"
                            value="monitor off"
                            severity="secondary"
                        />
                    </div>
                </template>
                <DataTable
                    v-model:selection="selectedProfile"
                    :value="pendingProfiles"
                    data-key="id"
                    size="small"
                    selection-mode="single"
                    :meta-key-selection="false"
                    class="cockpit-table"
                    :row-class="pendingProfileRowClass"
                >
                    <template #empty>No pending profiles.</template>
                    <Column header="ID" field="id" style="width: 6rem" />
                    <Column header="Product" field="product" />
                    <Column header="Questionnaire" field="hash" />
                    <Column header="Language" field="language" style="width: 7rem" />
                    <Column header="Started">
                        <template #body="{ data }">{{ formatStarted(data.started) }}</template>
                    </Column>
                    <Column header="Actions" style="width: 9rem">
                        <template #body="{ data }">
                            <Button
                                icon="pi pi-replay"
                                rounded outlined severity="secondary" size="small" class="mr-1"
                                aria-label="Regenerate"
                                @click.stop="regenerateProfile(data)"
                            />
                            <Button
                                v-if="!isGroupChart(data.product)"
                                icon="pi pi-calculator"
                                rounded outlined severity="secondary" size="small" class="mr-1"
                                aria-label="Recalculate"
                                @click.stop="recalculateProfile(data)"
                            />
                            <Button
                                icon="pi pi-trash"
                                rounded outlined severity="danger" size="small"
                                aria-label="Delete profile"
                                @click.stop="deleteProfile(data)"
                            />
                        </template>
                    </Column>
                </DataTable>
            </Panel>

            <!-- Filter for the GitLab-based panels below: MRs, main pipelines, versions, SonarQube -->
            <div class="flex flex-wrap items-center gap-2 mb-2" data-cy="cockpit-filter">
                <IconField>
                    <InputIcon class="pi pi-search" />
                    <InputText
                        v-model="filterText"
                        placeholder="Filter by project, service or MR"
                        aria-label="Filter merge requests, pipelines, versions and SonarQube by name"
                        size="small"
                        class="w-80"
                        data-cy="cockpit-filter-text"
                    />
                </IconField>
                <SelectButton
                    v-model="filterArea"
                    :options="AREA_OPTIONS"
                    option-label="label"
                    option-value="value"
                    :allow-empty="false"
                    size="small"
                    aria-label="Project area"
                    data-cy="cockpit-filter-area"
                />
                <Button
                    v-if="filterActive"
                    icon="pi pi-filter-slash"
                    label="Clear"
                    text
                    size="small"
                    data-cy="cockpit-filter-clear"
                    @click="clearFilter"
                />
            </div>

            <!-- 2. Merge Requests -->
            <Panel class="mb-2">
                <template #header>
                    <div class="flex items-center gap-2">
                        <span class="font-bold">Open Merge Requests</span>
                        <Tag :value="overview.mergeRequests.status" :severity="sourceSeverity(overview.mergeRequests.status)" />
                        <span class="text-content-muted text-sm">
                            {{ countLabel(filteredMergeRequests.length, overview.mergeRequests.data.length) }}
                        </span>
                    </div>
                </template>
                <div v-if="filteredMergeRequests.length" class="cockpit-list">
                    <div v-for="mr in filteredMergeRequests" :key="mr.webUrl" class="cockpit-row">
                        <span class="row-main">
                            <a :href="mr.webUrl" target="_blank" rel="noopener">!{{ mr.iid }}</a>
                            <span class="text-content-muted ml-1">{{ mr.project }}</span>
                            <span class="ml-2" :title="mr.title">{{ mr.title }}</span>
                        </span>
                        <span class="flex items-center gap-2 shrink-0">
                            <span class="text-content-muted text-sm whitespace-nowrap">
                                <i class="pi pi-user" style="font-size: 0.7rem" /> {{ mr.author }}
                            </span>
                            <Tag v-if="mr.draft" value="draft" severity="secondary" />
                            <Tag
                                v-if="mr.approved"
                                icon="pi pi-check"
                                severity="success"
                                :value="mr.approvedBy.length ? 'approved · ' + mr.approvedBy.join(', ') : 'approved'"
                            />
                            <Tag v-else icon="pi pi-clock" value="not approved" severity="secondary" />
                            <Tag v-if="mr.autoMerge" icon="pi pi-forward" value="auto-merge" severity="info" />
                            <a v-if="mr.pipelineUrl" :href="mr.pipelineUrl" target="_blank" rel="noopener">
                                <Tag :value="mr.pipelineStatus ?? 'none'" :severity="pipelineSeverity(mr.pipelineStatus)" />
                            </a>
                            <Tag v-else :value="mr.pipelineStatus ?? 'none'" :severity="pipelineSeverity(mr.pipelineStatus)" />
                        </span>
                    </div>
                </div>
                <div v-else-if="overview.mergeRequests.data.length" class="cockpit-empty">
                    No merge requests match the filter.
                </div>
                <div v-else class="cockpit-empty">No open merge requests.</div>
            </Panel>

            <!-- 3. Main-branch pipelines — latest per project, failing ones first, click to open -->
            <Panel class="mb-2">
                <template #header>
                    <div class="flex items-center gap-2">
                        <span class="font-bold">Main pipelines</span>
                        <Tag :value="overview.pipelines.status" :severity="sourceSeverity(overview.pipelines.status)" />
                        <span class="text-content-muted text-sm">
                            {{ countLabel(filteredPipelines.length, overview.pipelines.data.length) }}
                        </span>
                    </div>
                </template>
                <DataTable
                    v-model:selection="selectedPipeline"
                    :value="filteredPipelines"
                    size="small"
                    selection-mode="single"
                    :meta-key-selection="false"
                    class="cockpit-table"
                >
                    <template #empty>
                        {{
                            filterActive && overview.pipelines.data.length
                                ? "No pipelines match the filter."
                                : "No main-branch pipelines found."
                        }}
                    </template>
                    <Column header="Project">
                        <template #body="{ data }">
                            <a v-if="data.webUrl" :href="data.webUrl" target="_blank" rel="noopener" @click.stop>
                                {{ data.component ?? data.project }}
                            </a>
                            <span v-else>{{ data.component ?? data.project }}</span>
                            <div v-if="data.component" class="text-content-muted text-sm">
                                {{ data.project }}
                            </div>
                        </template>
                    </Column>
                    <Column header="Status" style="width: 9rem">
                        <template #body="{ data }">
                            <a v-if="data.webUrl" :href="data.webUrl" target="_blank" rel="noopener" @click.stop>
                                <Tag :value="data.status ?? 'none'" :severity="pipelineSeverity(data.status)" />
                            </a>
                            <Tag v-else :value="data.status ?? 'none'" :severity="pipelineSeverity(data.status)" />
                        </template>
                    </Column>
                    <Column header="Updated">
                        <template #body="{ data }">{{ formatTime(data.updatedAt) }}</template>
                    </Column>
                </DataTable>
            </Panel>

            <!-- 4. Versions (left) + Support (right) -->
            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-12 lg:col-span-6">
                    <Panel class="mb-2 h-full">
                        <template #header>
                            <div class="flex items-center gap-2">
                                <span class="font-bold">Versions</span>
                                <Tag :value="overview.versions.status" :severity="sourceSeverity(overview.versions.status)" />
                            </div>
                        </template>
                        <Tabs v-if="versionEnvs.length" :value="versionEnvs[0]" class="versions-table">
                            <TabList>
                                <Tab v-for="env in versionEnvs" :key="env" :value="env">{{ env }}</Tab>
                            </TabList>
                            <TabPanels>
                                <TabPanel v-for="env in versionEnvs" :key="env" :value="env">
                                    <DataTable
                                        v-model:selection="selectedVersion"
                                        :value="versionsForEnv(env)"
                                        data-key="service"
                                        size="small"
                                        selection-mode="single"
                                        :meta-key-selection="false"
                                        class="cockpit-table"
                                    >
                                        <template #empty>No services match the filter.</template>
                                        <Column header="Service" field="service" />
                                        <Column header="Version">
                                            <template #body="{ data }">
                                                <RouterLink
                                                    v-if="data.reachable && data.version"
                                                    :to="{
                                                        name: 'ReleaseNotes',
                                                        query: { service: data.service, version: data.version },
                                                    }"
                                                    class="version-release-notes"
                                                    title="View release notes for this version"
                                                    @click.stop
                                                    >{{ data.version }}</RouterLink
                                                >
                                                <span v-else-if="data.reachable">—</span>
                                                <Tag v-else value="unreachable" severity="secondary" />
                                            </template>
                                        </Column>
                                        <Column header="Commit">
                                            <template #body="{ data }">
                                                <a
                                                    v-if="data.reachable && data.commitUrl && data.commit"
                                                    :href="data.commitUrl"
                                                    target="_blank"
                                                    rel="noopener"
                                                    class="version-commit"
                                                    @click.stop
                                                    >{{ data.commit.slice(0, 8) }}</a
                                                >
                                                <span v-else-if="data.reachable && data.commit" class="version-commit">
                                                    {{ data.commit.slice(0, 8) }}
                                                </span>
                                            </template>
                                        </Column>
                                    </DataTable>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                        <div v-else class="cockpit-empty">No version targets configured.</div>
                    </Panel>
                </div>
                <div class="col-span-12 lg:col-span-6">
                    <Panel class="mb-2 h-full">
                        <template #header>
                            <div class="flex items-center gap-2">
                                <span class="font-bold">Support (POS · Dringender IT-Support)</span>
                                <Tag :value="overview.jira.pos.status" :severity="sourceSeverity(overview.jira.pos.status)" />
                                <span class="text-content-muted text-sm">({{ overview.jira.pos.data.length }})</span>
                            </div>
                        </template>
                        <DataTable
                            v-model:selection="selectedPos"
                            :value="overview.jira.pos.data"
                            data-key="key"
                            size="small"
                            selection-mode="single"
                            :meta-key-selection="false"
                            class="cockpit-table"
                        >
                            <template #empty>No open support tickets.</template>
                            <Column header="Key" style="width: 6rem">
                                <template #body="{ data }">
                                    <a :href="data.url" target="_blank" rel="noopener" @click.stop>{{ data.key }}</a>
                                </template>
                            </Column>
                            <Column header="Summary" field="summary" />
                            <Column header="Assignee" style="width: 9rem">
                                <template #body="{ data }">
                                    <span :class="{ 'text-content-muted italic': !data.assignee }">
                                        {{ data.assignee ?? "Unassigned" }}
                                    </span>
                                </template>
                            </Column>
                            <Column header="Priority" field="priority" style="width: 7rem" />
                            <Column header="Status" style="width: 9rem">
                                <template #body="{ data }">
                                    <Tag :value="data.status" :severity="statusCategorySeverity(data.statusCategory)" />
                                </template>
                            </Column>
                        </DataTable>
                    </Panel>
                </div>
            </div>

            <!-- 4. Dev board (RDT), full width -->
            <Panel class="mb-2">
                <template #header>
                    <div class="flex items-center gap-2">
                        <span class="font-bold">Dev board (RDT)</span>
                        <Tag :value="overview.jira.rdt.status" :severity="sourceSeverity(overview.jira.rdt.status)" />
                        <span class="text-content-muted text-sm">({{ overview.jira.rdt.data.length }})</span>
                    </div>
                </template>
                <DataTable
                    v-model:selection="selectedRdt"
                    :value="overview.jira.rdt.data"
                    data-key="key"
                    size="small"
                    selection-mode="single"
                    :meta-key-selection="false"
                    class="cockpit-table"
                >
                    <template #empty>Nothing in progress on the board.</template>
                    <Column header="Key" style="width: 6rem">
                        <template #body="{ data }">
                            <a :href="data.url" target="_blank" rel="noopener" @click.stop>{{ data.key }}</a>
                        </template>
                    </Column>
                    <Column header="Summary" field="summary" />
                    <Column header="Assignee" style="width: 12rem">
                        <template #body="{ data }">
                            <span :class="{ 'text-content-muted italic': !data.assignee }">
                                {{ data.assignee ?? "Unassigned" }}
                            </span>
                        </template>
                    </Column>
                    <Column header="Status" style="width: 10rem">
                        <template #body="{ data }">
                            <Tag :value="data.status" :severity="statusCategorySeverity(data.statusCategory)" />
                        </template>
                    </Column>
                </DataTable>
            </Panel>

            <!-- 5. Pending participants, full width, with delete -->
            <Panel class="mb-2">
                <template #header>
                    <div class="flex items-center gap-2">
                        <span class="font-bold">Pending participants</span>
                        <span class="text-content-muted text-sm">({{ pendingParticipants.length }})</span>
                    </div>
                </template>
                <DataTable
                    v-model:selection="selectedParticipant"
                    :value="pendingParticipants"
                    data-key="id"
                    size="small"
                    selection-mode="single"
                    :meta-key-selection="false"
                    class="cockpit-table"
                >
                    <template #empty>No pending participants.</template>
                    <Column header="First name" field="firstName" />
                    <Column header="Last name" field="name" />
                    <Column header="Email">
                        <template #body="{ data }">
                            <a :href="`/participants?email=${data.email}`" @click.stop>{{ data.email }}</a>
                        </template>
                    </Column>
                    <Column header="Product" style="width: 8rem">
                        <template #body="{ data }">
                            <Tag v-if="data.selectedProduct" severity="info" :value="data.selectedProduct.toUpperCase()" />
                        </template>
                    </Column>
                    <Column header="Status" field="status" style="width: 8rem" />
                    <Column header="Completed" style="width: 11rem">
                        <template #body="{ data }">{{ formatTime(data.completedDate) }}</template>
                    </Column>
                    <Column header="Actions" style="width: 10rem">
                        <template #body="{ data }">
                            <Button
                                icon="pi pi-replay"
                                rounded outlined severity="secondary" size="small" class="mr-1"
                                aria-label="Regenerate"
                                @click.stop="regenerateProfile(toProfile(data), false)"
                            />
                            <Button
                                icon="pi pi-calculator"
                                rounded outlined severity="secondary" size="small" class="mr-1"
                                aria-label="Recalculate"
                                @click.stop="recalculateProfile(toProfile(data))"
                            />
                            <Button
                                icon="pi pi-trash"
                                rounded outlined severity="danger" size="small"
                                aria-label="Delete participant"
                                @click.stop="confirmDeleteParticipant(data)"
                            />
                        </template>
                    </Column>
                </DataTable>
            </Panel>

            <!-- SonarQube — quality gate + issue counts per project, worst-first, links to the dashboard -->
            <Panel class="mb-2">
                <template #header>
                    <div class="flex items-center gap-2">
                        <span class="font-bold">SonarQube</span>
                        <Tag :value="overview.sonarqube.status" :severity="sourceSeverity(overview.sonarqube.status)" />
                        <span class="text-content-muted text-sm">
                            {{ countLabel(filteredSonar.length, overview.sonarqube.data.length) }}
                        </span>
                    </div>
                </template>
                <DataTable
                    v-model:selection="selectedSonar"
                    :value="filteredSonar"
                    data-key="key"
                    size="small"
                    selection-mode="single"
                    :meta-key-selection="false"
                    class="cockpit-table sonar-table"
                >
                    <template #empty>
                        {{
                            filterActive && overview.sonarqube.data.length
                                ? "No SonarQube projects match the filter."
                                : "No SonarQube projects configured."
                        }}
                    </template>
                    <Column header="Project">
                        <template #body="{ data }">
                            <a :href="data.url" target="_blank" rel="noopener" @click.stop>{{ data.name }}</a>
                        </template>
                    </Column>
                    <Column header="Quality gate" style="width: 8rem">
                        <template #body="{ data }">
                            <Tag :value="data.qualityGateStatus ?? 'none'" :severity="gateSeverity(data.qualityGateStatus)" />
                        </template>
                    </Column>
                    <Column header="Bugs" style="width: 6rem">
                        <template #body="{ data }">
                            <Tag v-if="data.bugs" :value="String(data.bugs)" severity="danger" />
                            <span v-else>{{ data.bugs ?? "—" }}</span>
                        </template>
                    </Column>
                    <Column header="Vulnerabilities" style="width: 8rem">
                        <template #body="{ data }">
                            <Tag v-if="data.vulnerabilities" :value="String(data.vulnerabilities)" severity="danger" />
                            <span v-else>{{ data.vulnerabilities ?? "—" }}</span>
                        </template>
                    </Column>
                    <Column header="Smells" style="width: 6rem">
                        <template #body="{ data }">{{ data.codeSmells ?? "—" }}</template>
                    </Column>
                    <Column header="Coverage" style="width: 7rem">
                        <template #body="{ data }">{{ formatCoverage(data.coverage) }}</template>
                    </Column>
                </DataTable>
            </Panel>

            <!-- 6. Tools & infrastructure -->
            <Panel header="Tools & infrastructure" class="mb-4">

                <div class="flex flex-wrap gap-2">
                    <a :href="GITLAB_URL" target="_blank" rel="noopener">
                        <Button label="GitLab" icon="pi pi-external-link" outlined />
                    </a>
                    <a :href="JIRA_URL" target="_blank" rel="noopener">
                        <Button label="Jira" icon="pi pi-external-link" outlined />
                    </a>
                    <a :href="OPENOBSERVE_URL" target="_blank" rel="noopener">
                        <Button label="OpenObserve logs" icon="pi pi-external-link" outlined />
                    </a>
                    <a :href="UPTIME_KUMA_URL" target="_blank" rel="noopener">
                        <Button label="Uptime Kuma" icon="pi pi-external-link" outlined />
                    </a>
                    <a :href="DOCKHAND_URL" target="_blank" rel="noopener">
                        <Button label="Dockhand" icon="pi pi-external-link" outlined />
                    </a>
                    <a :href="NEXUS_URL" target="_blank" rel="noopener">
                        <Button label="Nexus" icon="pi pi-external-link" outlined />
                    </a>
                    <a :href="SONARQUBE_URL" target="_blank" rel="noopener">
                        <Button label="SonarQube" icon="pi pi-external-link" outlined />
                    </a>
                    <a v-if="KEYCLOAK_URL" :href="KEYCLOAK_URL" target="_blank" rel="noopener">
                        <Button label="Keycloak" icon="pi pi-external-link" outlined />
                    </a>
                </div>
            </Panel>
        </template>
    </div>
</template>

<style scoped>
.dev-cockpit {
    padding-top: 1.5rem;
}
.dev-cockpit a {
    color: var(--primary-color);
    text-decoration: none;
}
.dev-cockpit a:hover {
    text-decoration: underline;
}

/* Compact, tightly-stacked single-line rows for the MR list. */
.cockpit-list {
    display: flex;
    flex-direction: column;
}
.cockpit-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.3rem 0.4rem;
    border-radius: 4px;
    border-bottom: 1px solid var(--surface-border);
}
.cockpit-row:last-child {
    border-bottom: none;
}
.cockpit-row:hover {
    background-color: color-mix(in srgb, var(--text-color) 8%, transparent);
}
/* Let a long MR title use up to two lines, then cut with an ellipsis (full title on hover). */
.cockpit-row .row-main {
    flex: 1 1 auto;
    min-width: 0;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
}
.cockpit-empty {
    color: var(--text-color-secondary);
    padding: 0.3rem 0.25rem;
}

/* On narrow screens (mobile) let a wide table scroll horizontally inside its panel instead of being
   cut off. The fixed-width columns give the table a natural min-width; the container scrolls. */
.cockpit-table :deep(.p-datatable-table-container),
.cockpit-table :deep(.p-datatable-wrapper) {
    overflow-x: auto;
}

/* Row hover on every table. Selection (selection-mode="single") gives the click-to-keep highlight;
   this adds the on-hover highlight, targeting the cells so it sits above the theme's cell background. */
.cockpit-table :deep(.p-datatable-tbody > tr) {
    transition: background-color 0.15s;
}
.cockpit-table :deep(.p-datatable-tbody > tr:hover > td) {
    background-color: color-mix(in srgb, var(--text-color) 8%, transparent);
    cursor: pointer;
}

.version-commit {
    font-family: var(--font-family-monospace, monospace);
    font-size: 0.8rem;
}
/* Version + commit sit on the right, like the old matrix. */
.versions-table :deep(.p-datatable-tbody td:nth-child(n + 2)),
.versions-table :deep(.p-datatable-thead th:nth-child(n + 2)) {
    text-align: right;
}

/* SonarQube counts + coverage (columns 3+) read better right-aligned; project + gate stay left. */
.sonar-table :deep(.p-datatable-tbody td:nth-child(n + 3)),
.sonar-table :deep(.p-datatable-thead th:nth-child(n + 3)) {
    text-align: right;
}
</style>
